import { useEffect, useState } from 'react';
import { BettingScreen } from '../screens/BettingScreen';
import { HomeScreen } from '../screens/HomeScreen';
import { LobbyScreen } from '../screens/LobbyScreen';
import { OverScreen } from '../screens/OverScreen';
import { ResultScreen } from '../screens/ResultScreen';
import { RoundScreen } from '../screens/RoundScreen';
import { Banner } from '../ui/Banner';
import { Button } from '../ui/Button';
import { MusicVolume } from '../ui/MusicVolume';
import { SoundToggle } from '../ui/SoundToggle';
import { ThemeToggle } from '../ui/ThemeToggle';
import { useConnection } from './connection';
import { useSound } from './sound';
import { useTheme } from './theme';
import s from './app.module.scss';

/**
 * L'aiguillage.
 *
 * Il n'y a pas de routeur, et il n'en faut pas : l'ecran affiche est une
 * consequence de la PHASE du serveur, jamais de l'URL. C'est ce qui garantit
 * que deux joueurs de la meme table voient la meme chose — un routeur
 * autoriserait un client a s'afficher ailleurs que la ou le salon en est.
 *
 * L'URL porte quand meme le code du salon, pour qu'un lien s'envoie et que le
 * rafraichissement ne perde pas le code. C'est une commodite, pas un etat.
 */
export function App() {
  const { state, actions } = useConnection();
  const theme = useTheme();
  const sound = useSound();
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  useEffect(() => {
    if (!state.room) return;
    window.history.replaceState(null, '', `/?r=${state.room}`);
  }, [state.room]);

  // Partie lancee et pas finie : le siege ne se reprend plus, donc un clic
  // malheureux ne doit pas suffire. Au salon ou a l'ecran final, on ne perd
  // rien — un depart immediat.
  const inGame = state.phase === 'betting' || state.phase === 'playing' || state.phase === 'result';

  const requestLeave = () => {
    if (inGame) setConfirmingLeave(true);
    else actions.leave();
  };

  // La confirmation n'a de sens que pour la partie qui l'a demandee : si elle
  // se termine ou si la socket tombe pendant qu'elle est ouverte, on la ferme.
  useEffect(() => {
    if (!inGame || state.status !== 'joined') setConfirmingLeave(false);
  }, [inGame, state.status]);

  const screen = () => {
    if (state.status !== 'joined') {
      return <HomeScreen connecting={state.status === 'connecting'} onJoin={actions.join} />;
    }
    switch (state.phase) {
      case 'betting':
        return <BettingScreen state={state} actions={actions} />;
      case 'playing':
        return <RoundScreen state={state} actions={actions} />;
      case 'result':
        return <ResultScreen state={state} />;
      case 'over':
        return <OverScreen state={state} actions={actions} />;
      case 'lobby':
      case 'loadout':
        return <LobbyScreen state={state} actions={actions} />;
    }
  };

  return (
    <div className={s.shell}>
      <div className="crt" aria-hidden="true" />

      <nav className={s.topbar}>
        <span className={s.brand}>COMPLETE LA REFERENCE</span>
        <div className={s.topbarRight}>
          {state.room ? <span className={s.roomTag}>{state.room}</span> : null}
          <MusicVolume />
          <SoundToggle on={sound.on} onToggle={sound.toggle} />
          <ThemeToggle choice={theme.choice} onCycle={theme.cycle} />
          {state.status === 'joined' ? (
            <Button tone="ghost" onClick={requestLeave}>
              Quitter
            </Button>
          ) : null}
        </div>
      </nav>

      {screen()}

      {confirmingLeave ? (
        <div className={s.confirm} role="alertdialog" aria-label="Quitter la partie">
          <p className={s.confirmText}>Quitter la partie ?</p>
          <p className={s.confirmHint}>
            Ta place est perdue : tu ne pourras pas revenir a cette table. La partie continue sans
            toi.
          </p>
          <div className={s.confirmActions}>
            <Button tone="action" size="lg" onClick={() => setConfirmingLeave(false)}>
              Rester
            </Button>
            <Button tone="ghost" onClick={actions.leave}>
              Quitter
            </Button>
          </div>
        </div>
      ) : null}

      {/* La connexion perdue n'est pas un detail : le serveur n'accepte de
          rejoindre un salon QU'EN phase de salon. Une coupure en pleine partie
          ne se rattrape donc pas, et le dire franchement vaut mieux qu'une
          tentative de reconnexion qui echouera. */}
      {state.status === 'closed' ? (
        <div className={s.lost} role="alertdialog" aria-label="Connexion perdue">
          <p className={s.lostText}>Connexion perdue.</p>
          <p className={s.lostHint}>
            Ton siege reste a table. Un reessai reprend exactement ou tu en etais, manche en cours
            comprise.
          </p>
          <div className={s.lostActions}>
            <Button tone="action" size="lg" onClick={actions.retryConnection}>
              Reessayer
            </Button>
            <Button tone="ghost" onClick={actions.leave}>
              Retour a l'accueil
            </Button>
          </div>
        </div>
      ) : null}

      {state.error ? <Banner message={state.error} onDismiss={actions.dismissError} /> : null}
    </div>
  );
}
